<template>
  <div class="monarch-transactions">
    <div class="header">
      <h1>Monarch Transactions</h1>
      <div class="header-actions">
        <div class="filters">
          <select v-model="days" @change="loadTransactions">
            <option :value="7">Last 7 days</option>
            <option :value="30">Last 30 days</option>
            <option :value="60">Last 60 days</option>
            <option :value="90">Last 90 days</option>
            <option :value="120">Last 120 days</option>
            <option :value="180">Last 180 days</option>
          </select>
          <label class="filter-checkbox">
            <input type="checkbox" v-model="retailersOnly" @change="loadTransactions" />
            Known retailers only
          </label>
        </div>
        <button @click="syncFromMonarch" :disabled="syncing" class="btn-sync">
          {{ syncing ? 'Syncing...' : 'Sync from Monarch' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading">Loading transactions...</div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="loadTransactions" class="btn-retry">Retry</button>
    </div>

    <div v-else-if="transactions.length === 0" class="empty-state">
      <p>No cached transactions. Click "Sync from Monarch" to fetch.</p>
    </div>

    <div v-else>
      <p class="result-count">{{ transactions.length }} transactions found</p>
      <table class="txn-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Merchant</th>
            <th>Amount</th>
            <th>Category</th>
            <th>Account</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="txn in transactions" :key="txn.id">
            <td>{{ txn.date }}</td>
            <td>{{ txn.merchant || txn.originalDescription || '—' }}</td>
            <td :class="txn.amount < 0 ? 'amount-negative' : 'amount-positive'">
              ${{ Math.abs(txn.amount).toFixed(2) }}
            </td>
            <td>{{ txn.category || '—' }}</td>
            <td>{{ txn.account || '—' }}</td>
            <td class="notes-cell">{{ txn.notes || '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { syncAPI } from '../services/api'

const transactions = ref([])
const loading = ref(false)
const syncing = ref(false)
const error = ref(null)
const days = ref(30)
const retailersOnly = ref(true)
const knownRetailerPatterns = ['amazon', 'amzn', 'target', 'walmart', 'wal-mart', 'costco', 'best buy', 'bestbuy']

onMounted(() => {
  loadTransactions()
})

const loadTransactions = async () => {
  loading.value = true
  error.value = null
  try {
    const params = { days: days.value }
    const response = await syncAPI.getMonarchTransactions(params)
    let txns = response.data.transactions || []
    if (retailersOnly.value) {
      txns = txns.filter(t => {
        const merchant = (t.merchant || '').toLowerCase()
        return knownRetailerPatterns.some(p => merchant.includes(p))
      })
    }
    transactions.value = txns
  } catch (err) {
    console.error('Error loading Monarch transactions:', err)
    error.value = err.response?.data?.error || 'Failed to load transactions'
  } finally {
    loading.value = false
  }
}

const syncFromMonarch = async () => {
  syncing.value = true
  error.value = null
  try {
    const data = { days: days.value }
    const response = await syncAPI.syncMonarchTransactions(data)
    let txns = response.data.transactions || []
    if (retailersOnly.value) {
      txns = txns.filter(t => {
        const merchant = (t.merchant || '').toLowerCase()
        return knownRetailerPatterns.some(p => merchant.includes(p))
      })
    }
    transactions.value = txns
  } catch (err) {
    console.error('Error syncing Monarch transactions:', err)
    error.value = err.response?.data?.error || 'Failed to sync from Monarch'
  } finally {
    syncing.value = false
  }
}
</script>

<style scoped>
.monarch-transactions {
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

h1 { color: #2c3e50; margin: 0; }

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.filters {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filters select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: #2c3e50;
  cursor: pointer;
}

.btn-sync {
  padding: 0.5rem 1rem;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
}
.btn-sync:hover:not(:disabled) { background: #219a52; }
.btn-sync:disabled { opacity: 0.6; cursor: not-allowed; }

.loading, .empty-state, .error-state {
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
}

.error-state { color: #c0392b; }

.btn-retry {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.result-count {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.txn-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.txn-table th {
  background: #f8f9fa;
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #ecf0f1;
}

.txn-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #ecf0f1;
  font-size: 0.9rem;
  color: #2c3e50;
}

.txn-table tbody tr:hover {
  background-color: #f8f9fa;
}

.amount-negative { color: #e74c3c; font-weight: 600; }
.amount-positive { color: #27ae60; font-weight: 600; }

.notes-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
